import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '蘑菇视频首页', item: 'https://www.moguvideo.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.moguvideo.com/about' },
      ],
    },
    {
      '@type': 'AboutPage',
      name: '关于蘑菇视频 - 短视频娱乐平台',
      description: '蘑菇视频是一款深受用户喜爱的短视频娱乐平台。了解蘑菇视频的品牌故事、用户口碑评价，以及联系方式。',
      url: 'https://www.moguvideo.com/about',
    },
    {
      '@type': 'Organization',
      name: '蘑菇视频',
      url: 'https://www.moguvideo.com',
      description: '蘑菇视频是一款深受用户喜爱的短视频娱乐平台，海量高清视频内容随心看。',
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'support@moguvideo.com',
        availableLanguage: ['Chinese'],
      },
    },
  ],
};

const testimonials = [
  {
    name: '小美',
    avatar: 'https://readdy.ai/api/search-image?query=Friendly%20young%20woman%20avatar%20icon%20minimalist%20flat%20design%20warm%20pink%20tone%20simple%20background&width=100&height=100&seq=testimonial-av01&orientation=squarish',
    role: '大学生',
    content: '蘑菇视频是我每天必刷的App，内容特别丰富，搞笑视频能让我笑一整天。推荐算法也很准，总能看到我喜欢的内容！',
    rating: 5,
  },
  {
    name: '阿强',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20man%20avatar%20icon%20minimalist%20flat%20design%20warm%20green%20tone%20simple%20background&width=100&height=100&seq=testimonial-av02&orientation=squarish',
    role: '美食博主',
    content: '作为一个美食创作者，蘑菇视频给了我很大的展示空间。平台的创作工具很强大，粉丝互动也很活跃，超爱这里！',
    rating: 5,
  },
  {
    name: '张阿姨',
    avatar: 'https://readdy.ai/api/search-image?query=Middle%20aged%20woman%20avatar%20icon%20minimalist%20flat%20design%20warm%20orange%20tone%20simple%20background&width=100&height=100&seq=testimonial-av03&orientation=squarish',
    role: '退休教师',
    content: '女儿教我用蘑菇视频，一用就爱上了。生活小妙招、美食教程特别实用，现在买菜做饭都离不开它了！',
    rating: 5,
  },
  {
    name: '大明',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20professional%20man%20avatar%20icon%20minimalist%20flat%20design%20warm%20blue%20tone%20simple%20background&width=100&height=100&seq=testimonial-av04&orientation=squarish',
    role: '上班族',
    content: '通勤路上的最佳伴侣！视频加载快、内容优质，没有烦人的广告。已经推荐给全公司的同事了！',
    rating: 5,
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotInput && honeypotInput.value.trim() === '') {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9so8op4ne17ei0sdkvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const code = (parsed as { code?: string } | null)?.code;
      const metaMessage = (parsed as { meta?: { message?: string } } | null)?.meta?.message;
      const serverMsg = (parsed as { message?: string } | null)?.message;

      const isSpam = typeof responseText === 'string' && (responseText.includes('spam') || responseText.includes('form data is spam'));
      const serverMsgIsSpam = typeof serverMsg === 'string' && serverMsg.includes('spam');
      const metaMsgIsSpam = typeof metaMessage === 'string' && metaMessage.includes('spam');

      if (response.ok && code === 'OK' && !isSpam && !serverMsgIsSpam && !metaMsgIsSpam) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(metaMessage || serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于蘑菇视频 - 短视频娱乐平台 | 品牌介绍与联系方式"
        description="了解蘑菇视频的品牌故事和发展历程。蘑菇视频是一款深受用户喜爱的短视频娱乐平台，海量高清视频内容随心看。查看用户口碑评价，获取蘑菇视频联系方式。"
        keywords="蘑菇视频,蘑菇视频官网,蘑菇视频关于我们,蘑菇视频品牌介绍,蘑菇视频联系方式"
        canonicalUrl="https://www.moguvideo.com/about"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-5xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <a href="/" className="hover:text-primary-600 transition-colors">首页</a>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-950 font-medium">关于我们</span>
              </nav>
            </div>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-6 text-center">
                关于蘑菇视频
              </h1>
              <p className="text-base md:text-lg text-foreground-600 text-center max-w-2xl mx-auto mb-14 leading-relaxed">
                蘑菇视频成立于2020年，致力于为全球用户打造一个充满乐趣与创意的短视频娱乐平台。
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-4">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      蘑菇视频的创始团队来自国内知名互联网企业，他们发现短视频正在改变人们获取信息和娱乐的方式。于是，他们决定打造一个兼具趣味性、知识性和社交性的短视频平台——蘑菇视频。
                    </p>
                    <p>
                      "蘑菇"象征着自然、生命力和无限可能。正如蘑菇在雨后迅速生长，蘑菇视频也以惊人的速度成长，从最初的几千用户发展到今天的<strong>超过5000万用户</strong>，成为国内最受欢迎的短视频平台之一。
                    </p>
                    <p>
                      我们始终秉持"<strong>发现精彩，分享快乐</strong>"的核心理念，通过智能推荐算法，让每位用户都能找到自己感兴趣的内容。无论是搞笑段子、美食教程、时尚穿搭，还是科技资讯、旅行记录、音乐表演——蘑菇视频应有尽有。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20team%20collaborating%20in%20a%20bright%20open%20office%20space%20with%20natural%20light%2C%20warm%20wooden%20furniture%2C%20plants%2C%20minimalist%20design%2C%20positive%20and%20energetic%20atmosphere%2C%20clean%20composition&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="蘑菇视频团队"
                    title="蘑菇视频品牌故事 - 短视频娱乐平台"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
                {[
                  { number: '5000万+', label: '累计用户' },
                  { number: '10亿+', label: '日播放量' },
                  { number: '200万+', label: '内容创作者' },
                  { number: '100+', label: '覆盖国家地区' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4 bg-background-100 rounded-xl">
                    <div className="text-xl md:text-2xl font-bold text-primary-500 mb-1">{stat.number}</div>
                    <div className="text-xs md:text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="reputation" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3 text-center">
                用户口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-600 text-center mb-10">
                来自真实用户的评价与反馈
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {testimonials.map((item) => (
                  <div key={item.name} className="bg-background-50 rounded-xl p-5 md:p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: item.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{item.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-background-200 overflow-hidden">
                        <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-950">{item.name}</div>
                        <div className="text-xs text-foreground-500">{item.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3 text-center">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600 text-center mb-10">
                有任何问题或建议？欢迎与我们取得联系
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: 'ri-mail-line', title: '电子邮箱', content: 'support@moguvideo.com' },
                  { icon: 'ri-phone-line', title: '客服电话', content: '400-888-6666' },
                  { icon: 'ri-map-pin-line', title: '公司地址', content: '北京市海淀区中关村科技园' },
                ].map((item) => (
                  <div key={item.title} className="bg-background-100 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className={`${item.icon} text-primary-600`}></i>
                    </div>
                    <div className="text-xs text-foreground-500 mb-1">{item.title}</div>
                    <div className="text-sm font-medium text-foreground-950">{item.content}</div>
                  </div>
                ))}
              </div>

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-100 rounded-xl p-6 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入您的姓名"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="请输入您的邮箱"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    placeholder="请输入主题"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请输入您的留言内容（最多500字）"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? '提交中...' : '提交留言'}
                </button>

                {formStatus === 'success' && (
                  <div className="mt-4 p-3 bg-accent-100/70 text-accent-800 rounded-lg text-sm text-center">
                    感谢您的留言！我们会尽快回复您。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm text-center">
                    {formError || '提交失败，请稍后重试'}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4">
                加入蘑菇视频
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-xl mx-auto">
                下载蘑菇视频App，开启你的视频新世界。发现精彩内容，分享快乐瞬间！
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#"
                  className="flex items-center gap-3 px-8 py-3.5 bg-foreground-950 text-background-50 rounded-xl font-medium hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-apple-line text-xl"></i>
                  <div className="text-left">
                    <div className="text-[10px] opacity-70">Download on the</div>
                    <div className="text-sm font-semibold">App Store</div>
                  </div>
                </a>
                <a
                  href="#"
                  className="flex items-center gap-3 px-8 py-3.5 border border-foreground-300 text-foreground-950 rounded-xl font-medium hover:bg-foreground-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-android-line text-xl"></i>
                  <div className="text-left">
                    <div className="text-[10px] opacity-70">Download for</div>
                    <div className="text-sm font-semibold">Android</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}